/**
 * Compares two dates and determines if they are equal within a specified tolerance in seconds
 *
 * @param d1 - First date (Date object, ISO string, or timestamp)
 * @param d2 - Second date (Date object, ISO string, or timestamp)
 * @param s - Tolerance in seconds - dates are considered equal if their difference is <= this value
 * @returns boolean - true if dates are equal within tolerance, false otherwise
 *
 * @example
 * // PIT and last_run comparison with 5 second tolerance
 * const appPit = "2024-07-21T18:57:31";
 * const lastRun = "2024-07-21T18:57:29.643000";
 * areDatesEqualWithinTolerance(appPit, lastRun, 5); // true (difference is ~1.36 seconds)
 *
 * @example
 * // Date objects with 10 second tolerance
 * const date1 = new Date("2024-07-21T18:57:31");
 * const date2 = new Date("2024-07-21T18:57:25");
 * areDatesEqualWithinTolerance(date1, date2, 10); // true (difference is 6 seconds)
 * areDatesEqualWithinTolerance(date1, date2, 5); // false (difference exceeds tolerance)
 */
export function areDatesEqualWithinTolerance(
  d1: Date | string | number | undefined,
  d2: Date | string | number | undefined,
  s: number = 0
): boolean {
  // Handle invalid arguments
  if (!d1 || !d2) {
    return false;
  }

  // Convert inputs to timestamps
  const timestamp1 = toTimestamp(d1);
  const timestamp2 = toTimestamp(d2);

  // Handle invalid dates
  if (timestamp1 === null || timestamp2 === null) {
    return false;
  }

  // Calculate absolute difference in milliseconds, then convert to seconds
  const diffInSeconds = Math.abs(timestamp1 - timestamp2) / 1000;

  // Return true if difference is within tolerance
  return diffInSeconds <= s;
}

/**
 * Helper function to convert various date formats to timestamps (milliseconds)
 */
function toTimestamp(value: Date | string | number): number | null {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }

  if (typeof value === "number") {
    // Handle timestamp (assume milliseconds if > 1e10, otherwise seconds)
    return value > 1e10 ? value : value * 1000;
  }

  if (typeof value === "string") {
    // If string has no timezone info, treat as UTC by appending 'Z'
    const dateString =
      value.includes("+") || value.includes("Z") || value.includes("-", 10)
        ? value // Has timezone info
        : value + "Z"; // Force UTC
    const date = new Date(dateString);
    const time = date.getTime();
    return Number.isNaN(time) ? null : time;
  }

  return null;
}
